<template>
  <div class="app-main-content">
    <div class="grid-view">
      <div class="main-content">
        <el-alert
          :title="$t('Clearing.UnlockLedgerTip')"
          type="info"
          :closable="false"
          show-icon
          class="unlock-ledger-alert"
        />
        <div class="operations">
          <div class="filters">
            <el-form :inline="true" @submit.native.prevent="onSearch">
              <el-form-item :label="$t('Clearing.UnlockLedgerBatchNo')">
                <el-input
                  v-model="filter.batchNo"
                  :placeholder="$t('CargoMsg.Cargo_PlsInput') + $t('Clearing.UnlockLedgerBatchNo')"
                  style="width: 180px"
                  clearable
                  @keyup.enter.native="onSearch"
                />
              </el-form-item>
              <el-form-item :label="$t('Clearing.UnlockLedgerApplyUser')">
                <el-input
                  v-model="filter.applyUser"
                  :placeholder="$t('CargoMsg.Cargo_PlsInput') + $t('Clearing.UnlockLedgerApplyUser')"
                  style="width: 150px"
                  clearable
                  @keyup.enter.native="onSearch"
                />
              </el-form-item>
              <el-form-item :label="$t('Clearing.UnlockLedgerStatus')">
                <el-select
                  v-model="filter.status"
                  :placeholder="$t('common.pleaseChoose')"
                  style="width: 140px"
                  clearable
                >
                  <el-option
                    v-for="item in statusOptions"
                    :key="item.value"
                    :value="item.value"
                    :label="item.label"
                  />
                </el-select>
              </el-form-item>
              <el-form-item>
                <el-button type="primary" @click="onSearch">{{ $t('common.btn.query') }}</el-button>
                <el-button @click="onReset">{{ $t('common.btn.reset') }}</el-button>
              </el-form-item>
            </el-form>
          </div>
        </div>
        <div v-loading="loading" class="section grid-wrapper">
          <el-table :data="rows" border height="100%" size="mini">
            <el-table-column
              prop="batchNo"
              :label="$t('Clearing.UnlockLedgerBatchNo')"
              min-width="150"
              show-overflow-tooltip
            />
            <el-table-column
              prop="applyUser"
              :label="$t('Clearing.UnlockLedgerApplyUser')"
              min-width="90"
              show-overflow-tooltip
            />
            <el-table-column
              prop="orderCount"
              :label="$t('Clearing.UnlockLedgerOrderCount')"
              width="80"
              align="center"
            />
            <el-table-column
              prop="reason"
              :label="$t('SeaExport.UnlockApplyReason')"
              min-width="200"
              show-overflow-tooltip
            />
            <el-table-column
              :label="$t('Clearing.UnlockLedgerDeclaredImpact')"
              width="120"
              align="right"
            >
              <template slot-scope="scope">{{ fmtAmount(scope.row.declaredProfitImpact) }}</template>
            </el-table-column>
            <el-table-column
              :label="$t('Clearing.UnlockLedgerBeforeProfit')"
              width="110"
              align="right"
            >
              <template slot-scope="scope">{{ fmtAmount(scope.row.unlockBeforeProfit) }}</template>
            </el-table-column>
            <el-table-column
              :label="$t('Clearing.UnlockLedgerAfterProfit')"
              width="110"
              align="right"
            >
              <template slot-scope="scope">{{ fmtAmount(scope.row.unlockAfterProfit) }}</template>
            </el-table-column>
            <el-table-column
              :label="$t('Clearing.UnlockLedgerActualImpact')"
              width="120"
              align="right"
            >
              <template slot-scope="scope">{{ fmtAmount(scope.row.actualProfitImpact) }}</template>
            </el-table-column>
            <el-table-column
              :label="$t('Clearing.UnlockLedgerStatus')"
              width="90"
              align="center"
            >
              <template slot-scope="scope">
                <el-tag :type="statusTagType(scope.row.status)" size="mini">
                  {{ statusText(scope.row.status) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column
              :label="$t('Clearing.UnlockLedgerRelockTime')"
              width="150"
              align="center"
            >
              <template slot-scope="scope">{{ fmtTime(scope.row.planRelockTime) }}</template>
            </el-table-column>
            <el-table-column
              :label="$t('Clearing.UnlockLedgerApplyTime')"
              width="150"
              align="center"
            >
              <template slot-scope="scope">{{ fmtTime(scope.row.applyTime) }}</template>
            </el-table-column>
            <el-table-column
              :label="$t('Clearing.UnlockLedgerManualRemarks')"
              min-width="150"
            >
              <template slot-scope="scope">
                <el-tooltip
                  v-if="scope.row.manualLockRemarks"
                  :content="formatManualRemarks(scope.row.manualLockRemarks)"
                  placement="top"
                  popper-class="unlock-manual-remarks-tooltip"
                >
                  <span class="manual-remarks-text">{{ formatManualRemarks(scope.row.manualLockRemarks) }}</span>
                </el-tooltip>
                <span v-else class="--">--</span>
              </template>
            </el-table-column>
          </el-table>
        </div>
        <div class="pagination-wrapper">
          <cargo-pagination
            v-show="total > 0"
            :total="total"
            :page.sync="filter.pageNum"
            :limit.sync="filter.pageSize"
            :page-sizes="[10, 20, 30, 50, 100]"
            @pagination="toQuery"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getLoading } from '@/utils/utilBaseElement'
import { pageUnlockApply } from '@/api/booking/unlockApply'
import DateUtils from '@/utils/dateUtils'

export default {
  name: 'UnlockLedger',
  components: {},
  data() {
    return {
      loading: false,
      rows: [],
      total: 0,
      filter: {
        batchNo: '',
        applyUser: '',
        status: null,
        pageNum: 1,
        pageSize: 20
      }
    }
  },
  computed: {
    // 台账状态筛选项：0审批中/1解锁中/2已重锁/3已拒绝
    statusOptions() {
      return ['0', '1', '2', '3'].map(code => ({ value: code, label: this.statusText(code) }))
    }
  },
  created() {
    this.toQuery()
  },
  methods: {
    statusText(status) {
      const texts = {
        '0': this.$t('Clearing.UnlockLedgerStatus0'),
        '1': this.$t('Clearing.UnlockLedgerStatus1'),
        '2': this.$t('Clearing.UnlockLedgerStatus2'),
        '3': this.$t('Clearing.UnlockLedgerStatus3')
      }
      return texts[status] || '--'
    },
    statusTagType(status) {
      // 0审批中warning 1解锁中primary(默认蓝) 2已重锁success 3已拒绝danger
      const types = { '0': 'warning', '1': '', '2': 'success', '3': 'danger' }
      return Object.prototype.hasOwnProperty.call(types, status) ? types[status] : 'info'
    },
    fmtAmount(value) {
      if (value === null || value === undefined || value === '') {
        return '--'
      }
      return Number(value).toFixed(2)
    },
    fmtTime(value) {
      return DateUtils.formatStandardDateTime(value) || '--'
    },
    // 后端 GROUP_CONCAT 返回格式为：JOB_NO|REMARK;;JOB_NO|REMARK;;...
    formatManualRemarks(raw) {
      if (!raw) return ''
      return raw.split(';;').map(item => {
        const idx = item.indexOf('|')
        if (idx < 0) return item
        return `${item.substring(0, idx)}: ${item.substring(idx + 1)}`
      }).join('\n')
    },
    onSearch() {
      this.filter.pageNum = 1
      this.toQuery()
    },
    onReset() {
      this.filter.batchNo = ''
      this.filter.applyUser = ''
      this.filter.status = null
      this.onSearch()
    },
    toQuery() {
      this.loading = true
      const loading = getLoading()
      pageUnlockApply({
        batchNo: this.filter.batchNo,
        applyUser: this.filter.applyUser,
        status: this.filter.status === null || this.filter.status === '' ? undefined : this.filter.status,
        pageNum: this.filter.pageNum,
        pageSize: this.filter.pageSize
      }).then(res => {
        const data = (res && res.data) || {}
        this.rows = data.list || []
        this.filter.pageNum = data.pageNum || this.filter.pageNum
        this.filter.pageSize = data.pageSize || this.filter.pageSize
        this.total = Number(data.total || 0)
      }).finally(() => {
        this.loading = false
        loading.close()
      })
    }
  }
}
</script>

<style scoped>
.unlock-ledger-alert {
  margin: 5px 0;
}
.manual-remarks-text {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
}
</style>

<!-- tooltip 挂载在 body 下，需非 scoped 样式 -->
<style>
.unlock-manual-remarks-tooltip {
  max-width: 380px;
  white-space: pre-line;
  line-height: 1.6;
}
</style>
